import type { AccessTokenProvider } from "../types";
import { newIdempotencyKey, requiresIdempotencyKey } from "./idempotency";
import { networkProblem, problemFromResponse, unexpectedResponseProblem } from "./problem";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string>;

export interface RequestOptions {
  /** Path template from the contract, e.g. `/fer/v1/alerts/{alert_id}/confirmation`. */
  path: string;
  pathParams?: Record<string, string>;
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Defaults to application/json; PATCH uses application/merge-patch+json. */
  contentType?: string;
  /** The form's key (useKeyedWrite), reused when the same request is resubmitted. */
  idempotencyKey?: string;
  /** Extra request headers (e.g. `If-Match`). */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Send no bearer token (public operations: local sign-in status and sign-in). */
  anonymous?: boolean;
}

export interface ApiClientConfig {
  /** Council API base URL from the council directory (via the Hub shell) or console-config.json. Never hard-coded. */
  apiBaseUrl: string;
  getAccessToken: AccessTokenProvider;
  /** Injected in tests and the dev harness. Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

/** A JSON reply and its `ETag` header (incident reports are saved with `If-Match`). */
export interface WithEtag<T> {
  data: T;
  etag: string | null;
}

/** application/json, application/geo+json, application/problem+json and other +json types. */
const JSON_TYPE = /^application\/([a-z0-9.-]+\+)?json\b/i;

/**
 * Thin fetch wrapper for the FER API: bearer token (realm or FER local token), Idempotency-Key on the writes the
 * contract requires it for, JSON in and out, RFC 9457 problems thrown as ApiProblem, and a non-JSON 2xx reply (a
 * captive portal page) thrown as `unexpected_response`, so no screen is left waiting.
 */
export class ApiClient {
  /** The API base URL (also keys this console's stored local credentials). */
  readonly base: string;
  private readonly getToken: AccessTokenProvider;
  private readonly fetchImpl: typeof fetch;

  constructor(cfg: ApiClientConfig) {
    this.base = cfg.apiBaseUrl.replace(/\/+$/, "");
    this.getToken = cfg.getAccessToken;
    this.fetchImpl = cfg.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  buildUrl(path: string, pathParams?: Record<string, string>, query?: Record<string, QueryValue>): string {
    const filled = path.replace(/\{(\w+)\}/g, (_, name: string) => {
      const v = pathParams?.[name];
      if (v === undefined) throw new Error(`Missing path parameter ${name} for ${path}`);
      return encodeURIComponent(v);
    });
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v === undefined || v === null || v === "") continue;
      // Arrays are `style: form, explode: false` in the contract: one comma-separated value.
      if (Array.isArray(v)) {
        if (v.length > 0) qs.set(k, v.join(","));
      } else qs.set(k, String(v));
    }
    const q = qs.toString();
    return `${this.base}${filled}${q ? `?${q}` : ""}`;
  }

  private async send(method: HttpMethod, opts: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: "application/json, application/problem+json",
      ...(opts.headers ?? {}),
    };
    if (!opts.anonymous) {
      const token = await this.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    if (opts.body !== undefined) headers["Content-Type"] = opts.contentType ?? "application/json";
    if (requiresIdempotencyKey(method, opts.path)) {
      headers["Idempotency-Key"] = opts.idempotencyKey ?? newIdempotencyKey();
    }
    let res: Response;
    try {
      res = await this.fetchImpl(this.buildUrl(opts.path, opts.pathParams, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      throw networkProblem(err);
    }
    if (!res.ok) throw await problemFromResponse(res);
    return res;
  }

  private async json<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    let text: string;
    try {
      text = await res.text();
    } catch (err) {
      throw networkProblem(err);
    }
    if (!text) return undefined as T;
    const type = res.headers.get("Content-Type") ?? "";
    if (type && !JSON_TYPE.test(type)) throw unexpectedResponseProblem(res.status);
    try {
      return JSON.parse(text) as T;
    } catch {
      throw unexpectedResponseProblem(res.status);
    }
  }

  async request<T>(method: HttpMethod, opts: RequestOptions): Promise<T> {
    return this.json<T>(await this.send(method, opts));
  }

  /** Like `request`, also answering the reply's `ETag`. */
  async requestWithEtag<T>(method: HttpMethod, opts: RequestOptions): Promise<WithEtag<T>> {
    const res = await this.send(method, opts);
    const etag = res.headers.get("ETag");
    return { data: await this.json<T>(res), etag };
  }

  /**
   * PUT bytes to a presigned upload URL returned by the FER API (`createUpload`). The bearer token is never sent
   * there, and only an https URL (http only for localhost in development builds) is followed.
   */
  async putPresigned(uploadUrl: string, headers: Record<string, string>, body: Blob): Promise<void> {
    let url: URL;
    try {
      url = new URL(uploadUrl);
    } catch {
      throw unexpectedResponseProblem(0);
    }
    const local = import.meta.env.DEV && (url.hostname === "localhost" || url.hostname === "127.0.0.1");
    if (url.protocol !== "https:" && !(local && url.protocol === "http:")) throw unexpectedResponseProblem(0);
    let res: Response;
    try {
      res = await this.fetchImpl(url.href, { method: "PUT", headers: { ...headers }, body });
    } catch (err) {
      throw networkProblem(err);
    }
    if (!res.ok) throw await problemFromResponse(res);
  }

  get<T>(path: string, opts: Omit<RequestOptions, "path" | "body"> = {}): Promise<T> {
    return this.request<T>("GET", { ...opts, path });
  }

  post<T>(path: string, opts: Omit<RequestOptions, "path"> = {}): Promise<T> {
    return this.request<T>("POST", { ...opts, path });
  }

  patch<T>(path: string, opts: Omit<RequestOptions, "path"> = {}): Promise<T> {
    return this.request<T>("PATCH", { contentType: "application/merge-patch+json", ...opts, path });
  }

  put<T>(path: string, opts: Omit<RequestOptions, "path"> = {}): Promise<T> {
    return this.request<T>("PUT", { ...opts, path });
  }

  delete<T>(path: string, opts: Omit<RequestOptions, "path" | "body"> = {}): Promise<T> {
    return this.request<T>("DELETE", { ...opts, path });
  }
}
